import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { User as SupabaseUser } from '@supabase/supabase-js';

// Definição dos tipos
type UserRole =
  | 'PRESIDENCIA'
  | 'DIRETORIA'
  | 'FINANCEIRO'
  | 'HOMOLOGACAO'
  | 'COLONIAL'
  | 'JURIDICO';
type UserStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'INACTIVE';

type User = {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  avatarUrl?: string;
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string, role: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

// Criação do contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Componente provider
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const isValidRole = (value: unknown): value is UserRole =>
    typeof value === 'string' &&
    ['PRESIDENCIA', 'DIRETORIA', 'FINANCEIRO', 'HOMOLOGACAO', 'COLONIAL', 'JURIDICO'].includes(
      value
    );

  const isValidStatus = (value: unknown): value is UserStatus =>
    typeof value === 'string' && ['PENDING', 'APPROVED', 'REJECTED', 'INACTIVE'].includes(value);

  const mapSessionUserToFallbackProfile = (supabaseUser: SupabaseUser): User | null => {
    const metadata = (supabaseUser.user_metadata ?? {}) as Record<string, unknown>;

    const email = supabaseUser.email ?? (metadata.email as string | undefined);
    if (!email) {
      return null;
    }

    const roleMetadata = metadata.role;
    const statusMetadata = metadata.status;
    const approvedFlag = typeof metadata.approved === 'boolean' ? metadata.approved : undefined;

    const fallbackRole: UserRole = isValidRole(roleMetadata) ? roleMetadata : 'HOMOLOGACAO';
    const fallbackStatus: UserStatus = isValidStatus(statusMetadata)
      ? statusMetadata
      : approvedFlag === false
        ? 'PENDING'
        : 'APPROVED';

    const metadataName = metadata.name as string | undefined;
    const fallbackName = metadataName ?? email.split('@')[0] ?? 'Usuário';

    const avatarUrl =
      (metadata.avatar_url as string | undefined) ??
      (metadata.avatarUrl as string | undefined) ??
      undefined;

    return {
      id: supabaseUser.id,
      email,
      name: fallbackName,
      role: fallbackRole,
      status: fallbackStatus,
      avatarUrl,
    };
  };

  const currentUserRef = React.useRef<User | null>(null);
  React.useEffect(() => {
    currentUserRef.current = user;
  }, [user]);

  // Cache mais inteligente com controle de estado
  const profileCache = React.useRef<Map<string, { profile: User; timestamp: number }>>(new Map());
  const CACHE_DURATION = 60000; // 1 minuto (mais tempo)
  const lastFetchTime = React.useRef<number>(0);
  const MIN_FETCH_INTERVAL = 5000; // Mínimo 5 segundos entre fetches

  const fetchUserProfile = async (
    supabaseUser: SupabaseUser,
    retryCount = 0,
    forceRefresh = false
  ): Promise<User | null> => {
    const MAX_RETRIES = 1; // Reduzido para 1 retry
    const TIMEOUT_MS = 10000; // 5 segundos (mais agressivo)
    const now = Date.now();

    try {
      // Verificar cache primeiro (se não for refresh forçado)
      if (!forceRefresh) {
        const cached = profileCache.current.get(supabaseUser.id);
        if (cached && now - cached.timestamp < CACHE_DURATION) {
          console.log('📦 Cache válido, usando perfil salvo');
          return cached.profile;
        }
      }

      // Evitar múltiplas requisições simultâneas
      if (now - lastFetchTime.current < MIN_FETCH_INTERVAL && !forceRefresh) {
        console.log('⏸️ Aguardando intervalo mínimo entre requisições');
        const cached = profileCache.current.get(supabaseUser.id);
        if (cached) {
          return cached.profile;
        }
      }

      lastFetchTime.current = now;

      // Timeout otimizado
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), TIMEOUT_MS)
      );

      // Query otimizada - apenas campos necessários
      const fetchPromise = supabase
        .from('profiles')
        .select('name, role, status, avatar_url')
        .eq('id', supabaseUser.id)
        .maybeSingle();

      const result = await Promise.race([fetchPromise, timeoutPromise]);

      const { data: profile, error } = result as any;

      if (error) {
        // Erro de permissão - tentar criar perfil
        if (error.code === 'PGRST116' || error.message?.includes('permission')) {
          console.warn('⚠️ Erro de permissão RLS. Tentando criar perfil...');
          const created = await tryCreateProfile(supabaseUser);
          if (created) return created;
          return null;
        }

        // Erro de rede - retry apenas uma vez
        if (
          retryCount < MAX_RETRIES &&
          (error.code === 'PGRST301' ||
            error.message?.includes('fetch') ||
            error.message?.includes('network'))
        ) {
          console.warn(`🔄 Erro de rede, tentando novamente em 2s...`);
          await new Promise((resolve) => setTimeout(resolve, 2000));
          return fetchUserProfile(supabaseUser, retryCount + 1, forceRefresh);
        }

        console.warn('⚠️ Erro ao buscar perfil:', error.message || error.code);
        return null;
      }

      if (!profile) {
        console.warn('⚠️ Perfil não encontrado. Tentando criar...');
        const created = await tryCreateProfile(supabaseUser);
        if (created) return created;
        return null;
      }

      // Resolver URL do avatar (converte caminho de Storage em URL pública)
      const resolveAvatarUrl = (value?: string | null) => {
        const raw = (value || '').trim();
        if (!raw) return '';
        if (/^https?:\/\//i.test(raw)) return raw; // já é URL completa
        if (raw.startsWith('avatars/')) {
          const { data } = supabase.storage.from('imgsinsecrj').getPublicUrl(raw);
          return data.publicUrl;
        }
        return raw;
      };

      // Criar objeto de usuário
      const userProfile: User = {
        id: supabaseUser.id,
        email: supabaseUser.email!,
        name: profile.name,
        role: profile.role,
        status: profile.status,
        avatarUrl: resolveAvatarUrl(profile.avatar_url),
      };

      // Salvar no cache
      profileCache.current.set(supabaseUser.id, {
        profile: userProfile,
        timestamp: Date.now(),
      });

      console.log('✅ Perfil carregado:', profile.name);
      return userProfile;
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('Timeout')) {
          if (retryCount < MAX_RETRIES) {
            console.warn(`⏱️ Timeout, tentando novamente...`);
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return fetchUserProfile(supabaseUser, retryCount + 1, forceRefresh);
          }
          console.warn('⚠️ Timeout persistente, usando cache ou fallback');
          // Tentar usar cache expirado como último recurso
          const expiredCache = profileCache.current.get(supabaseUser.id);
          if (expiredCache) {
            console.log('🔄 Usando cache expirado como fallback');
            return expiredCache.profile;
          }
        } else {
          console.error('❌ Erro ao buscar perfil:', error.message);
        }
      }
      return null;
    }
  };

  const tryCreateProfile = async (supabaseUser: SupabaseUser): Promise<User | null> => {
    try {
      const metadata = supabaseUser.user_metadata || {};
      const profileData = {
        id: supabaseUser.id,
        email: supabaseUser.email!,
        name: metadata.name || metadata.full_name || supabaseUser.email?.split('@')[0] || 'Usuário',
        role: metadata.role || 'HOMOLOGACAO',
        status: metadata.approved === false ? 'PENDING' : metadata.status || 'APPROVED',
        avatar_url: metadata.avatar_url || metadata.avatarUrl || null,
      };

      const { data, error } = await supabase
        .from('profiles')
        .upsert(profileData, { onConflict: 'id' })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          console.log('ℹ️ Perfil já existe');
          return null;
        }
        console.warn('⚠️ Erro ao criar perfil:', error.message);
        return null;
      }

      if (data) {
        console.log('✅ Perfil criado com sucesso!');
        const newProfile: User = {
          id: data.id,
          email: data.email,
          name: data.name,
          role: data.role,
          status: data.status,
          avatarUrl: data.avatar_url,
        };

        // Adicionar ao cache
        profileCache.current.set(supabaseUser.id, {
          profile: newProfile,
          timestamp: Date.now(),
        });

        return newProfile;
      }

      return null;
    } catch (error) {
      console.warn('⚠️ Erro ao criar perfil:', error);
      return null;
    }
  };

  useEffect(() => {
    const isMounted = { current: true };
    let authChangeProcessing = false;
    let currentUserId: string | null = null;

    const applySessionUser = async (sessionUser: SupabaseUser, forceRefresh = false) => {
      try {
        // Evitar processamento duplicado para o mesmo usuário
        // Usamos currentUserRef.current para evitar closure obsoleta
        if (!forceRefresh && currentUserId === sessionUser.id && currentUserRef.current) {
          console.log('🔄 Mesmo usuário já processado, ignorando');
          return;
        }

        currentUserId = sessionUser.id;
        const userProfile = await fetchUserProfile(sessionUser, 0, forceRefresh);

        if (!isMounted.current) {
          return;
        }

        if (userProfile) {
          setUser(userProfile);
          return;
        }

        // Se não conseguiu buscar o perfil, usa fallback dos metadados
        const fallbackProfile = mapSessionUserToFallbackProfile(sessionUser);

        if (fallbackProfile) {
          console.log('ℹ️ Usando metadados temporários (perfil será sincronizado)');
          setUser(fallbackProfile);
        } else {
          console.error('❌ Não foi possível criar perfil de usuário. Fazendo logout...');
          await supabase.auth.signOut();
          setUser(null);
          currentUserId = null;
        }
      } catch (error) {
        console.error('Erro ao aplicar sessão do usuário:', error);
        if (isMounted.current) {
          setUser(null);
          currentUserId = null;
        }
      }
    };

    const loadInitialSession = async () => {
      try {
        // Evita múltiplas chamadas simultâneas
        if (authChangeProcessing) {
          console.log('⏳ Autenticação já em processamento...');
          return;
        }

        authChangeProcessing = true;
        console.log('🔐 Verificando sessão...');

        // Timeout mais curto e agressivo - 5 segundos
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 15000)
        );

        const sessionPromise = supabase.auth.getSession();

        const result = await Promise.race([sessionPromise, timeoutPromise]);

        const {
          data: { session },
          error,
        } = result as any;

        if (error) {
          throw error;
        }

        if (session?.user) {
          console.log('✅ Sessão encontrada');
          await applySessionUser(session.user);
        } else {
          console.log('ℹ️ Nenhuma sessão ativa');
          if (isMounted.current) {
            setUser(null);
          }
        }
      } catch (error) {
        if (error instanceof Error && error.message === 'Timeout') {
          console.error('❌ Timeout ao carregar sessão. Verifique sua conexão.');
        } else {
          console.error('❌ Erro ao carregar sessão:', error);
        }
        if (isMounted.current) {
          setUser(null);
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
          console.log('✓ Autenticação concluída');
        }
        authChangeProcessing = false;
      }
    };

    loadInitialSession();

    // Escutar mudanças na autenticação com debounce
    let authChangeTimeout: NodeJS.Timeout | null = null;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Debounce para evitar múltiplas execuções rápidas
      if (authChangeTimeout) {
        clearTimeout(authChangeTimeout);
      }

      authChangeTimeout = setTimeout(async () => {
        if (!isMounted.current || authChangeProcessing) {
          return;
        }

        // Log do evento para debug
        console.log(`🔄 Auth event: ${event}`);

        authChangeProcessing = true;

        // Somente ativa o estado de loading se não tivermos um usuário NO ESTADO ATUAL (via Ref)
        // Isso evita que o AppLayout desmonte (unmount) toda a árvore de componentes e modais
        // durante refreshes de token em segundo plano ao alternar abas/minimizar o navegador.
        const shouldShowLoading = !currentUserRef.current && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION');

        if (shouldShowLoading) {
          setLoading(true);
        }

        try {
          if (session?.user) {
            // Forçar refresh apenas em eventos importantes
            const forceRefresh = event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED';
            await applySessionUser(session.user, forceRefresh);
          } else {
            console.log('🚪 Usuário deslogado');
            setUser(null);
            currentUserId = null;
            profileCache.current.clear();
          }
        } catch (error) {
          console.error('Erro ao processar mudança de autenticação:', error);
          setUser(null);
          currentUserId = null;
        } finally {
          if (isMounted.current) {
            setLoading(false);
          }
          authChangeProcessing = false;
        }
      }, 300); // 300ms de debounce
    });

    // Cleanup mais robusto
    return () => {
      isMounted.current = false;
      authChangeProcessing = false;
      currentUserId = null;

      if (authChangeTimeout) {
        clearTimeout(authChangeTimeout);
      }

      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Buscar perfil do usuário
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('status')
      .eq('email', email)
      .single();
    if (profileError) throw profileError;
    if (!profile || profile.status !== 'APPROVED') {
      throw new Error('Usuário ainda não aprovado pelo administrador.');
    }
  };

  const signUp = async (email: string, password: string, name: string, role: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          role,
          approved: false,
        },
      },
    });
    if (error) throw error;
    return data;
  };

  const refreshProfile = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.user) {
      console.log('🔄 Forçando atualização do perfil...');
      profileCache.current.delete(session.user.id);
      const profile = await fetchUserProfile(session.user, 0, true);
      if (profile) {
        setUser(profile);
      }
    }
  };

  const signOut = async () => {
    try {
      console.log('🚪 Fazendo logout...');

      // Limpar tudo antes do logout
      profileCache.current.clear();
      lastFetchTime.current = 0;
      setUser(null);

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      console.log('✅ Logout realizado com sucesso');
    } catch (error) {
      console.error('❌ Erro ao fazer logout:', error);
      // Mesmo com erro, limpar o estado local
      profileCache.current.clear();
      setUser(null);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        signUp,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
